import { MozartArc, SpritzArc } from '@arcxgame/contracts'
import { SynthAddressBook } from '@arcxgame/contracts/dist/arc-types/core'
import { useAsync } from '@arcxmoney/arcx-react'
import { Web3ReactContextInterface } from '@web3-react/core/dist/types'
import { Signer } from 'ethers'
import { getSpritzAddresses } from 'src/config'
import { injected } from 'src/constants/connectors'
import { ArcService } from 'src/services/ArcService'
import { Synth } from 'src/services/GraphService'
import { isNetworkSupported } from 'src/utils/isNetworkSupported'

export function useArcService(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  webContext: Web3ReactContextInterface<any>,
  synthGetter: (name: string) => Synth[],
  synths: Synth[],
  isPlaynetActive: boolean,
) {
  const [arc] = useAsync<ArcService>(async () => {
    if (localStorage.getItem('user_connected') === 'true') {
      await webContext.activate(injected)
    }

    if (isNetworkSupported(webContext.chainId)) {
      if (!webContext.library) {
        return null
      }

      const linkUsdSynth = synthGetter('LINKUSD')[0]
      const stableXSynths = synthGetter('STABLEX')

      const signer = await webContext.library.getSigner()

      if (stableXSynths.length === 0 || !linkUsdSynth || !signer) {
        return null
      }
      const spritzArc = await setupSpritzArc(
        signer,
        webContext.chainId,
        linkUsdSynth,
        isPlaynetActive,
      )

      const mozartArc = await setupMozartArc(signer, stableXSynths)
      return new ArcService(spritzArc, mozartArc)
    }
  }, [webContext.chainId, synths, isPlaynetActive])

  return arc
}

async function setupSpritzArc(
  signer: Signer,
  chainId: number,
  linkUsdSynth: Synth,
  isPlaynetActive: boolean,
) {
  if (chainId !== 1 && chainId !== 4) {
    // We only support rinkeby and mainnet
    throw Error(`Only chain ID 1 and 4 are supported. Got "${chainId}"`)
  }

  const _addressBook: SynthAddressBook = getSpritzAddresses(chainId, isPlaynetActive)
  const spritzArc = await SpritzArc.init(signer, {
    ..._addressBook,
    collateralAsset: linkUsdSynth.collateralAddress,
  })
  return spritzArc
}

async function setupMozartArc(signer: Signer, synths: Synth[]) {
  const mozartSynths = {}
  for (const synth of synths) {
    mozartSynths[synth.name] = synth.id
  }
  const mozartArc = await MozartArc.init(signer)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await mozartArc.addSynths(mozartSynths as any)
  return mozartArc
}
